import * as React from "react";
import Wrapper from "../../../components/TenantWrapper";
import { useNavigate, Link } from "react-router-dom";
import "./TenantFunctionality.css";
import { useSelector } from "react-redux";
import {
  Box,
  Breadcrumbs,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Avatar,
  Chip,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HistoryIcon from "@mui/icons-material/History";
import BuildIcon from "@mui/icons-material/Build";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function TenantFunctionality() {
  const open = useSelector((state) => state.sidebar.open);
  const navigate = useNavigate();

  const getId = window.location.pathname.replace("/tenant-functionality/", "");

  const actions = [
    {
      key: "agreement",
      title: "Rental Agreement",
      description:
        "View your digitally signed rental agreement, including terms, move-in date, and both parties' signatures.",
      icon: <DescriptionIcon sx={{ fontSize: 30 }} />,
      color: "#4f46e5",
      gradient: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
      buttonLabel: "View Agreement",
      onClick: () => navigate(`/tenant-agreement/${getId}`),
    },
    {
      key: "currentBill",
      title: "Current Bill",
      description:
        "Check this month's rent and utility charges, see the due date, and make your payment on time.",
      icon: <ReceiptLongIcon sx={{ fontSize: 30 }} />,
      color: "#ea580c",
      gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      buttonLabel: "View Current Bill",
      onClick: () => navigate(`/current-Bill/${getId}`),
    },
    {
      key: "previousBill",
      title: "Bill History",
      description:
        "Browse your complete payment history and download receipts for previously paid bills.",
      icon: <HistoryIcon sx={{ fontSize: 30 }} />,
      color: "#0284c7",
      gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      buttonLabel: "View History",
      onClick: () => navigate(`/previous-TenantBill/${getId}`),
    },
    {
      key: "maintenance",
      title: "Maintenance Request",
      description:
        "Facing an issue at your property? Submit a maintenance request and track its progress here.",
      icon: <BuildIcon sx={{ fontSize: 30 }} />,
      color: "#0d9488",
      gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)",
      buttonLabel: "Request Maintenance",
      onClick: () => navigate(`/tenant-maintenance-request/${getId}`),
    },
    {
      key: "unregister",
      title: "Move Out / Unregister",
      description:
        "Planning to move out? Submit an unregister request to formally end your tenancy with the landlord.",
      icon: <PersonRemoveIcon sx={{ fontSize: 30 }} />,
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      buttonLabel: "Request Unregister",
      buttonColor: "error",
      onClick: () => navigate(`/unregister-tenant-request/${getId}`),
    },
  ];

  return (
    <>
      <Wrapper open={open} />
      <div className={`${open ? "sidebar-open" : "sidebar-closed"} `}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            to="/tenant-dashboard"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              color: "inherit",
              textDecoration: "none",
            }}
          >
            <HomeIcon sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            Tenant Functionality
          </Typography>
        </Breadcrumbs>

        {/* Hero Header */}
        <Paper
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            color: "white",
            borderRadius: 3,
          }}
        >
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Manage Your Tenancy
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Everything you need for your rental — agreement, bills,
            maintenance, and more — all in one place.
          </Typography>
        </Paper>

        {/* Action Cards */}
        <Grid container spacing={3}>
          {actions.map((action) => (
            <Grid item xs={12} sm={6} md={4} key={action.key}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 3,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.15)",
                  },
                }}
                onClick={action.onClick}
              >
                <Box
                  sx={{
                    background: action.gradient,
                    px: 3,
                    pt: 3,
                    pb: 4,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      width: 56,
                      height: 56,
                    }}
                  >
                    {action.icon}
                  </Avatar>
                </Box>

                <CardContent sx={{ flexGrow: 1, pt: 2.5 }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {action.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color={action.buttonColor || "primary"}
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                    }}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1,
                      ...(action.buttonColor !== "error" && {
                        background: action.gradient,
                        "&:hover": {
                          background: action.gradient,
                          opacity: 0.9,
                        },
                      }),
                    }}
                  >
                    {action.buttonLabel}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </div>
    </>
  );
}
